import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Panel,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Node,
  type NodeTypes,
} from '@xyflow/react';
import { SmartBezierEdge } from '@jalez/react-flow-smart-edge';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccountTree as LayoutIcon,
  Save as SaveIcon,
  Schedule as DelayPaletteIcon,
  AltRoute as ConditionPaletteIcon,
  CompareArrows as AbTestPaletteIcon,
  StickyNote2 as NotePaletteIcon,
} from '@mui/icons-material';
import ThemeSelector from '../common/ThemeSelector';
import {
  useSmsPageDesign,
  smsPageTokens,
  smsEditorToolbarSx,
  smsBackIconButtonProps,
  smsFlowToolbarPanelSx,
  smsFlowCanvasChromeSx,
  smsFlowLeftColumnSx,
  smsFloatingSaveBarSx,
} from '../../theme/smsPageDesign';
import {
  MailingStartNode,
  MailingEndNode,
  MailingChannelNode,
  MailingDelayNode,
  MailingConditionNode,
  MailingAbTestNode,
  MailingNoteNode,
} from './FlowNodes';
import type { MailingChannel, MailingCampaign } from './mailingTypes';
import {
  CHANNEL_META,
  MAIL_EDITOR_MAX_DELAY_BLOCKS,
  MAIL_EDITOR_MAX_EVENT_CHANNEL_BLOCKS,
  MAIL_EDITOR_PALETTE_CHANNELS,
  MAIL_EDITOR_PALETTE_DISABLED_CHANNELS,
  countSmsViberDelayBlocks,
  defaultChannelNodeData,
  defaultNoteNodeData,
  visualChainIsEventTrigger,
} from './mailingTypes';
import { fetchVisualMailings, replaceVisualMailings, upsertVisualMailing } from './visualMailingsApi';
import { getLayoutedElements } from './flowLayout';
import { validateMailingFlow } from './flowGraphValidation';
import { stripEdgesSharingSourceHandle } from './flowConnectionRules';
import { isVisualChainViberSmsDyadNoEmail, linearizeVisualChannelSteps } from './visualChainLinearize';
import { MailingViberSmsCascadeContext } from './mailingViberSmsCascadeContext';
import { MailingFlowInspectorPanel } from './MailingFlowInspectorPanel';
import { deleteCampaign } from './mailingStorage';
import { apiGet } from '../../api/client';
import { prefetchSmsCascadeContext } from '../../api/smsCascadePrefetch';
import type { Campaign, Scenario } from '../../domain/mailingDomain';
import { campaignNamesLinkedToVisualChain, normalizeCampaignFromApi, resolveSubscriberListIdsForVisualChain, visualChainLockedByLaunchedCampaign } from '../../domain/mailingDomain';
import { MailingFlowLinkedCampaignsContext } from './mailingFlowLinkedCampaignsContext';
import { MailingFlowCampaignSubscriberListContext } from './mailingFlowCampaignSubscriberContext';
import { useTranslation } from 'react-i18next';
import i18next from '../../i18n/initI18n';
import LanguageSwitcher from '../common/LanguageSwitcher';

function formatLinkedCampaignsLine(names: string[]): string | null {
  if (!names.length) return null;
  if (names.length === 1) return i18next.t('flowEditor.linkedCampaignSingle', { name: names[0] });
  return i18next.t('flowEditor.linkedCampaignMany', { names: names.join(', ') });
}

const DRAG_MIME = 'application/x-mailing-palette';

const PUBLIC_BASE = process.env.PUBLIC_URL || '';

/** Иконки каналов в палитре (SVG в `public/mailing-palette/`). */
const MAILING_CHANNEL_PALETTE_SVG: Record<MailingChannel, string> = {
  push: `${PUBLIC_BASE}/mailing-palette/push.svg`,
  sms: `${PUBLIC_BASE}/mailing-palette/sms.svg`,
  email: `${PUBLIC_BASE}/mailing-palette/email.svg`,
  viber: `${PUBLIC_BASE}/mailing-palette/viber.svg`,
  waba: `${PUBLIC_BASE}/mailing-palette/waba.svg`,
};

function paletteChannelLead(ch: MailingChannel) {
  return (
    <Box
      component="img"
      src={MAILING_CHANNEL_PALETTE_SVG[ch]}
      alt=""
      sx={{ width: 20, height: 20, flexShrink: 0, objectFit: 'contain', display: 'block' }}
    />
  );
}

const nodeTypes: NodeTypes = {
  mailingStart: MailingStartNode,
  mailingEnd: MailingEndNode,
  mailingChannel: MailingChannelNode,
  mailingDelay: MailingDelayNode,
  mailingCondition: MailingConditionNode,
  mailingAbTest: MailingAbTestNode,
  mailingNote: MailingNoteNode,
};

const edgeTypes = { smart: SmartBezierEdge };

function PaletteItem({
  label,
  color,
  payload,
  disabled = false,
  lead,
}: {
  label: string;
  color: string;
  disabled?: boolean;
  /** Если задан — вместо цветной точки слева (как у узла на холсте). */
  lead?: React.ReactNode;
  payload:
    | { kind: 'channel'; channel: MailingChannel }
    | { kind: 'delay' }
    | { kind: 'condition' }
    | { kind: 'abtest' }
    | { kind: 'note' };
}) {
  const { isSmsLight } = useSmsPageDesign();
  const pageTokens = smsPageTokens(isSmsLight);
  const onDragStart = (e: React.DragEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <Paper
      elevation={0}
      draggable={!disabled}
      onDragStart={onDragStart}
      sx={{
        px: 1.5,
        py: 1,
        cursor: disabled ? 'not-allowed' : 'grab',
        borderRadius: '8px',
        border: `1px solid ${pageTokens.outlineBorder}`,
        bgcolor: pageTokens.white,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        opacity: disabled ? 0.55 : 1,
        userSelect: 'none',
        ...(!disabled ? { '&:active': { cursor: 'grabbing' } } : {}),
      }}
    >
      {lead ?? <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />}
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
    </Paper>
  );
}

function FlowCanvas({
  campaign,
  name,
  linkedContextLoaded,
  readOnly,
  onSaved,
  onExitAfterSuccessfulSave,
  onExitFromReadOnly,
  onSaveError,
}: {
  campaign: MailingCampaign;
  name: string;
  /** До true Save заблокирован, чтобы не сохранить уже заблокированную запущенной кампанией цепочку до проверки контекста. */
  linkedContextLoaded: boolean;
  /** Цепочку нельзя менять (привязка к кампании после запуска). */
  readOnly?: boolean;
  onSaved: (saved: MailingCampaign) => void;
  /** После успешного «Сохранить и выйти» — на главную, вкладка «Мои кампании». */
  onExitAfterSuccessfulSave: () => void;
  /** Выход из режима только просмотра — список цепочек. */
  onExitFromReadOnly: () => void;
  onSaveError: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const { isSmsLight } = useSmsPageDesign();
  const { screenToFlowPosition } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState(campaign.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(campaign.edges);
  const frozen = Boolean(readOnly);
  const [inspectorNodeId, setInspectorNodeId] = useState<string | null>(null);
  const [paletteLimitMsg, setPaletteLimitMsg] = useState<string | null>(null);
  const [viberSmsCascadeLinked, setViberSmsCascadeLinked] = useState(
    () => campaign.viberSmsCascadeLinked ?? false
  );

  useEffect(() => {
    void prefetchSmsCascadeContext();
  }, []);

  useEffect(() => {
    setViberSmsCascadeLinked(campaign.viberSmsCascadeLinked ?? false);
  }, [campaign.id, campaign.viberSmsCascadeLinked]);

  const inspectorNode = useMemo(
    () => (inspectorNodeId ? nodes.find((n) => n.id === inspectorNodeId) ?? null : null),
    [nodes, inspectorNodeId]
  );

  const smsViberDelayCounts = useMemo(() => countSmsViberDelayBlocks(nodes), [nodes]);
  const isEventChain = useMemo(() => visualChainIsEventTrigger(nodes), [nodes]);
  const isViberSmsDyad = useMemo(() => isVisualChainViberSmsDyadNoEmail(nodes, edges), [nodes, edges]);
  const firstSmsViberChannelNodeId = useMemo(() => {
    if (!isViberSmsDyad) return null;
    const steps = linearizeVisualChannelSteps(nodes, edges);
    return steps[0]?.nodeId ?? null;
  }, [isViberSmsDyad, nodes, edges]);
  const viberSmsCascadeCtxValue = useMemo(
    () => ({
      firstChannelNodeId: firstSmsViberChannelNodeId,
      viberSmsCascadeLinked,
      setViberSmsCascadeLinked,
      readOnly: frozen,
    }),
    [firstSmsViberChannelNodeId, viberSmsCascadeLinked, frozen]
  );

  useEffect(() => {
    if (inspectorNodeId && !nodes.some((n) => n.id === inspectorNodeId)) {
      setInspectorNodeId(null);
    }
  }, [nodes, inspectorNodeId]);

  useEffect(() => {
    setNodes(campaign.nodes);
    setEdges(campaign.edges);
  }, [campaign.id, campaign.updatedAt, setNodes, setEdges, campaign.nodes, campaign.edges]);

  const onConnect = useCallback(
    (c: Connection) => {
      if (frozen) return;
      setEdges((eds) =>
        addEdge({ ...c, type: 'smart' }, stripEdgesSharingSourceHandle(eds, c))
      );
    },
    [frozen, setEdges]
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (frozen) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, [frozen]);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      if (frozen) return;
      e.preventDefault();
      const raw = e.dataTransfer.getData(DRAG_MIME);
      if (!raw) return;
      let payload:
        | { kind: 'channel'; channel: MailingChannel }
        | { kind: 'delay' }
        | { kind: 'condition' }
        | { kind: 'abtest' }
        | { kind: 'note' };
      try {
        payload = JSON.parse(raw);
      } catch {
        return;
      }
      if (payload.kind === 'channel' && !MAIL_EDITOR_PALETTE_CHANNELS.includes(payload.channel)) {
        return;
      }
      if (payload.kind === 'channel' && MAIL_EDITOR_PALETTE_DISABLED_CHANNELS.has(payload.channel)) {
        return;
      }
      const limits = countSmsViberDelayBlocks(nodes);
      const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const id = `n-${Date.now().toString(36)}`;
      if (payload.kind === 'delay') {
        if (isEventChain) {
          setPaletteLimitMsg(t('flowEditor.paletteDelayEventDisabled'));
          return;
        }
        if (limits.delay >= MAIL_EDITOR_MAX_DELAY_BLOCKS) {
          setPaletteLimitMsg(t('flowEditor.paletteDelayMaxLimit', { max: MAIL_EDITOR_MAX_DELAY_BLOCKS }));
          return;
        }
        const node: Node = {
          id,
          type: 'mailingDelay',
          position: pos,
          data: { value: 15, unit: 'minutes' },
        };
        setNodes((nds) => nds.concat(node));
        return;
      }
      if (payload.kind === 'condition') {
        return;
      }
      if (payload.kind === 'abtest') {
        return;
      }
      if (payload.kind === 'note') {
        const node: Node = {
          id,
          type: 'mailingNote',
          position: pos,
          data: defaultNoteNodeData(),
        };
        setNodes((nds) => nds.concat(node));
        return;
      }
      if (payload.channel === 'sms') {
        const maxSms = isEventChain ? MAIL_EDITOR_MAX_EVENT_CHANNEL_BLOCKS : 1;
        if (limits.sms >= maxSms) {
          setPaletteLimitMsg(
            isEventChain
              ? t('flowEditor.paletteEventChannelLimit', { channel: 'SMS', max: maxSms })
              : t('flowEditor.paletteSmsLimit')
          );
          return;
        }
      }
      if (payload.channel === 'viber') {
        const maxViber = isEventChain ? MAIL_EDITOR_MAX_EVENT_CHANNEL_BLOCKS : 1;
        if (limits.viber >= maxViber) {
          setPaletteLimitMsg(
            isEventChain
              ? t('flowEditor.paletteEventChannelLimit', { channel: 'Viber', max: maxViber })
              : t('flowEditor.paletteViberLimit')
          );
          return;
        }
      }
      if (payload.channel === 'email') {
        const maxEmail = isEventChain ? MAIL_EDITOR_MAX_EVENT_CHANNEL_BLOCKS : 1;
        if (limits.email >= maxEmail) {
          setPaletteLimitMsg(
            isEventChain
              ? t('flowEditor.paletteEventChannelLimit', { channel: 'Email', max: maxEmail })
              : t('flowEditor.paletteEmailLimit')
          );
          return;
        }
      }
      const node: Node = {
        id,
        type: 'mailingChannel',
        position: pos,
        data: defaultChannelNodeData(payload.channel),
      };
      setNodes((nds) => nds.concat(node));
    },
    [screenToFlowPosition, setNodes, frozen, nodes, t, isEventChain]
  );

  const handleLayout = useCallback(() => {
    if (frozen) return;
    const { nodes: ln, edges: le } = getLayoutedElements(nodes, edges, 'LR');
    setNodes(ln);
    setEdges(le);
  }, [nodes, edges, setNodes, setEdges, frozen]);

  const handleSave = useCallback(() => {
    if (frozen) return;
    const next: MailingCampaign = {
      ...campaign,
      name: name.trim() || campaign.name,
      nodes,
      edges,
      updatedAt: Date.now(),
    };
    if (isViberSmsDyad) {
      next.viberSmsCascadeLinked = viberSmsCascadeLinked;
    } else {
      delete next.viberSmsCascadeLinked;
    }
    const validation = validateMailingFlow(next);
    if (!validation.ok) {
      onSaveError(validation.errors.slice(0, 8).join(' | '));
      return;
    }
    void (async () => {
      try {
        await upsertVisualMailing(next);
        onSaved(next);
        onExitAfterSuccessfulSave();
      } catch (e) {
        onSaveError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [
    campaign,
    edges,
    frozen,
    isViberSmsDyad,
    name,
    nodes,
    onSaved,
    onExitAfterSuccessfulSave,
    onSaveError,
    viberSmsCascadeLinked,
  ]);

  return (
    <>
      <Snackbar
        open={Boolean(paletteLimitMsg)}
        autoHideDuration={4500}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setPaletteLimitMsg(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 56, md: 64 } }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setPaletteLimitMsg(null)}
          sx={{ width: '100%', maxWidth: { xs: 'min(420px, 92vw)', sm: 480 } }}
        >
          {paletteLimitMsg}
        </Alert>
      </Snackbar>
      {frozen ? (
        <Alert severity="info" sx={{ borderRadius: 1, mx: 2, mt: 1.5, flexShrink: 0 }}>
          {t('flowEditor.chainReadOnlyBanner')}
        </Alert>
      ) : null}
      <MailingViberSmsCascadeContext.Provider value={viberSmsCascadeCtxValue}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: 220,
            flexShrink: 0,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            ...smsFlowLeftColumnSx(isSmsLight),
            ...(frozen ? { opacity: 0.55, pointerEvents: 'none' } : {}),
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            {t('flowEditor.paletteChannelsTitle')}
          </Typography>
          {MAIL_EDITOR_PALETTE_CHANNELS.map((ch) => {
            const m = CHANNEL_META[ch];
            const devDisabled = MAIL_EDITOR_PALETTE_DISABLED_CHANNELS.has(ch);
            const maxCh = isEventChain ? MAIL_EDITOR_MAX_EVENT_CHANNEL_BLOCKS : 1;
            const atSmsLimit = ch === 'sms' && smsViberDelayCounts.sms >= maxCh;
            const atViberLimit = ch === 'viber' && smsViberDelayCounts.viber >= maxCh;
            const atEmailLimit = ch === 'email' && smsViberDelayCounts.email >= maxCh;
            const limitDisabled = atSmsLimit || atViberLimit || atEmailLimit;
            const disabled = devDisabled || limitDisabled;
            const limitTooltip = atSmsLimit
              ? isEventChain
                ? t('flowEditor.paletteEventChannelLimit', { channel: 'SMS', max: maxCh })
                : t('flowEditor.paletteSmsLimit')
              : atViberLimit
                ? isEventChain
                  ? t('flowEditor.paletteEventChannelLimit', { channel: 'Viber', max: maxCh })
                  : t('flowEditor.paletteViberLimit')
                : atEmailLimit
                  ? isEventChain
                    ? t('flowEditor.paletteEventChannelLimit', { channel: 'Email', max: maxCh })
                    : t('flowEditor.paletteEmailLimit')
                  : undefined;
            const item = (
              <PaletteItem
                label={t(`channels.${ch}`)}
                color={m.color}
                disabled={disabled}
                lead={paletteChannelLead(ch)}
                payload={{ kind: 'channel', channel: ch }}
              />
            );
            if (devDisabled) {
              return (
                <Tooltip key={ch} title={t('flowEditor.paletteDisabledChannelTooltip')} placement="right">
                  <Box component="span" sx={{ display: 'block', width: 1 }}>
                    {item}
                  </Box>
                </Tooltip>
              );
            }
            if (limitDisabled && limitTooltip) {
              return (
                <Tooltip key={ch} title={limitTooltip} placement="right">
                  <Box component="span" sx={{ display: 'block', width: 1 }}>
                    {item}
                  </Box>
                </Tooltip>
              );
            }
            return <React.Fragment key={ch}>{item}</React.Fragment>;
          })}
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mt: 1 }}>
            {t('flowEditor.paletteLogicTitle')}
          </Typography>
          {isEventChain ? (
            <Tooltip title={t('flowEditor.paletteDelayEventDisabled')} placement="right">
              <Box component="span" sx={{ display: 'block', width: 1 }}>
                <PaletteItem
                  label={t('flowEditor.paletteDelay')}
                  color="#90A4AE"
                  disabled
                  payload={{ kind: 'delay' }}
                  lead={<DelayPaletteIcon sx={{ fontSize: 20, color: 'secondary.main', flexShrink: 0 }} />}
                />
              </Box>
            </Tooltip>
          ) : smsViberDelayCounts.delay >= MAIL_EDITOR_MAX_DELAY_BLOCKS ? (
            <Tooltip title={t('flowEditor.paletteDelayMaxLimit', { max: MAIL_EDITOR_MAX_DELAY_BLOCKS })} placement="right">
              <Box component="span" sx={{ display: 'block', width: 1 }}>
                <PaletteItem
                  label={t('flowEditor.paletteDelay')}
                  color="#90A4AE"
                  disabled
                  payload={{ kind: 'delay' }}
                  lead={<DelayPaletteIcon sx={{ fontSize: 20, color: 'secondary.main', flexShrink: 0 }} />}
                />
              </Box>
            </Tooltip>
          ) : (
            <PaletteItem
              label={t('flowEditor.paletteDelay')}
              color="#90A4AE"
              payload={{ kind: 'delay' }}
              lead={<DelayPaletteIcon sx={{ fontSize: 20, color: 'secondary.main', flexShrink: 0 }} />}
            />
          )}
          <Tooltip title={t('flowEditor.paletteDisabledLogicTooltip')} placement="right">
            <Box component="span" sx={{ display: 'block', width: 1 }}>
              <PaletteItem
                label={t('flowEditor.paletteCondition')}
                color="#00897B"
                disabled
                payload={{ kind: 'condition' }}
                lead={<ConditionPaletteIcon sx={{ fontSize: 20, color: '#00897B', flexShrink: 0 }} />}
              />
            </Box>
          </Tooltip>
          <Tooltip title={t('flowEditor.paletteDisabledLogicTooltip')} placement="right">
            <Box component="span" sx={{ display: 'block', width: 1 }}>
              <PaletteItem
                label={t('flowEditor.paletteAbTest')}
                color="#5E35B1"
                disabled
                payload={{ kind: 'abtest' }}
                lead={<AbTestPaletteIcon sx={{ fontSize: 20, color: '#5E35B1', flexShrink: 0 }} />}
              />
            </Box>
          </Tooltip>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mt: 1 }}>
            {t('flowEditor.paletteStylingTitle')}
          </Typography>
          <PaletteItem
            label={t('flowEditor.paletteNote')}
            color="#F9A825"
            payload={{ kind: 'note' }}
            lead={<NotePaletteIcon sx={{ fontSize: 20, color: '#F9A825', flexShrink: 0 }} />}
          />
        </Box>
        <Box
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, p: 2, pt: 0, minHeight: 0 }}
        >
          <Box sx={{ flex: 1, minHeight: 360, position: 'relative', minWidth: 0 }}>
            <Box
              sx={{ position: 'absolute', inset: 0, ...smsFlowCanvasChromeSx(isSmsLight) }}
              onDragOver={onDragOver}
              onDrop={onDrop}
            >
              <ReactFlow
                className={isSmsLight ? undefined : 'dark'}
                style={{ width: '100%', height: '100%' }}
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                nodesDraggable={!frozen}
                nodesConnectable={!frozen}
                edgesReconnectable={!frozen}
                deleteKeyCode={frozen ? null : 'Backspace'}
                onNodeClick={(_, n) => {
                  if (n.type === 'mailingEnd') {
                    setInspectorNodeId(null);
                    return;
                  }
                  setInspectorNodeId(n.id);
                }}
                onPaneClick={() => setInspectorNodeId(null)}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                defaultEdgeOptions={{ type: 'smart' }}
                fitView
                minZoom={0.4}
                maxZoom={1.5}
                proOptions={{ hideAttribution: true }}
              >
              <MiniMap pannable zoomable />
              <Controls />
              <Panel position="top-center" style={{ marginTop: 8 }}>
                <Paper
                  elevation={0}
                  sx={{
                    ...smsFlowToolbarPanelSx(isSmsLight),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<LayoutIcon />}
                    onClick={handleLayout}
                    disabled={frozen}
                  >
                    {t('flowEditor.layoutAuto')}
                  </Button>
                </Paper>
              </Panel>
              <Background variant={BackgroundVariant.Dots} gap={16} size={1} color={isSmsLight ? '#d0d0d0' : '#444'} />
              <Panel position="bottom-center" style={{ marginBottom: 16 }}>
                <Paper elevation={0} sx={smsFloatingSaveBarSx(isSmsLight)}>
                  {frozen ? (
                    <Button variant="outlined" color="inherit" onClick={() => onExitFromReadOnly()}>
                      {t('flowEditor.backToChainsList')}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={
                        linkedContextLoaded ? (
                          <SaveIcon />
                        ) : (
                          <CircularProgress size={16} color="inherit" />
                        )
                      }
                      onClick={handleSave}
                      disabled={!linkedContextLoaded}
                    >
                      {t('flowEditor.saveAndExit')}
                    </Button>
                  )}
                </Paper>
              </Panel>
              </ReactFlow>
            </Box>
            <MailingFlowInspectorPanel
              node={inspectorNode}
              open={Boolean(inspectorNodeId)}
              onClose={() => setInspectorNodeId(null)}
              readOnly={frozen}
            />
          </Box>
        </Box>
      </Box>
      </MailingViberSmsCascadeContext.Provider>
    </>
  );
}

const MailingFlowEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isUnsavedDraft = searchParams.get('draft') === '1';
  const { isSmsLight, theme } = useSmsPageDesign();
  const pageTokens = smsPageTokens(isSmsLight);
  const [campaign, setCampaign] = useState<MailingCampaign | null>(null);
  const [name, setName] = useState('');
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [linkedCampaignLine, setLinkedCampaignLine] = useState<string | null>(null);
  const [linkedSubscriberListIds, setLinkedSubscriberListIds] = useState<readonly string[] | null>(null);
  /** Цепочку нельзя редактировать — привязка к кампании после запуска. */
  const [chainReadOnly, setChainReadOnly] = useState(false);
  /** false до тех пор, пока не подгружены кампании/сценарии — на это время Save показывает loading. */
  const [linkedContextLoaded, setLinkedContextLoaded] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoadErr(null);
    void (async () => {
      try {
        const list = await fetchVisualMailings();
        if (cancelled) return;
        const c = list.find((x) => x.id === id);
        if (!c) {
          navigate({ pathname: '/', search: '?tab=scenarios' }, { replace: true });
          return;
        }
        setCampaign(c);
        setName(c.name);
      } catch (e) {
        if (!cancelled) {
          setLoadErr(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  useEffect(() => {
    if (!campaign?.id) {
      setLinkedCampaignLine(null);
      setLinkedSubscriberListIds(null);
      setLinkedContextLoaded(false);
      setChainReadOnly(false);
      return;
    }
    let cancelled = false;
    setLinkedContextLoaded(false);
    void (async () => {
      try {
        const [campaignRowsRaw, scenariosList] = await Promise.all([
          apiGet<Campaign[]>('/api/campaigns'),
          apiGet<Scenario[]>('/api/scenarios'),
        ]);
        if (cancelled) return;
        const campaignRows = campaignRowsRaw.map(normalizeCampaignFromApi);
        const names = campaignNamesLinkedToVisualChain(campaign.id, campaignRows, scenariosList);
        setLinkedCampaignLine(formatLinkedCampaignsLine(names));
        setLinkedSubscriberListIds(resolveSubscriberListIdsForVisualChain(campaign.id, campaignRows));
        setChainReadOnly(visualChainLockedByLaunchedCampaign(campaign.id, campaignRows));
      } catch {
        if (!cancelled) {
          setLinkedCampaignLine(null);
          setLinkedSubscriberListIds(null);
          setChainReadOnly(false);
        }
      } finally {
        if (!cancelled) setLinkedContextLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaign?.id, campaign?.updatedAt]);

  const onSaved = useCallback((saved: MailingCampaign) => {
    setCampaign(saved);
    setSaveErr(null);
  }, []);

  const goToChainsList = useCallback(() => {
    navigate({ pathname: '/', search: '?tab=scenarios' });
  }, [navigate]);

  const goToCampaignsList = useCallback(() => {
    navigate({ pathname: '/' });
  }, [navigate]);

  const discardDraftAndGoBack = useCallback(() => {
    void (async () => {
      if (isUnsavedDraft && campaign?.id) {
        try {
          const list = await fetchVisualMailings();
          await replaceVisualMailings(deleteCampaign(list, campaign.id));
        } catch (e) {
          setSaveErr(e instanceof Error ? e.message : String(e));
          return;
        }
      }
      navigate({ pathname: '/', search: '?tab=scenarios' });
    })();
  }, [campaign?.id, isUnsavedDraft, navigate]);

  const handleSaveErrSnackbarClose = useCallback(
    (_?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') return;
      setSaveErr(null);
    },
    []
  );

  const toolbar = useMemo(
    () => (
      <Box sx={smsEditorToolbarSx(isSmsLight)}>
        <Tooltip title={t('flowEditor.tooltipBack')}>
          <IconButton
            aria-label={t('common.back')}
            onClick={discardDraftAndGoBack}
            {...smsBackIconButtonProps}
            sx={{
              ...smsBackIconButtonProps.sx,
              color: theme.palette.mode === 'light' ? 'primary.main' : undefined,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <TextField
          size="small"
          label={t('flowEditor.fieldName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={chainReadOnly}
          sx={{ minWidth: 220, flex: 1, maxWidth: 420 }}
        />
        <Box sx={{ flex: 1 }} />
        <ThemeSelector />
        <LanguageSwitcher />
      </Box>
    ),
    [discardDraftAndGoBack, isSmsLight, name, theme.palette.mode, t, chainReadOnly]
  );

  if (!campaign) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 1.5,
          px: 2,
        }}
      >
        <Typography color="text.secondary" textAlign="center">
          {loadErr ? t('flowEditor.loadFailed') : t('flowEditor.loading')}
        </Typography>
        {loadErr ? (
          <>
            <Typography variant="body2" color="error" textAlign="center">
              {loadErr}
            </Typography>
            <Button variant="outlined" onClick={goToChainsList}>
              {t('flowEditor.backToChainsList')}
            </Button>
          </>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
        bgcolor: pageTokens.bg,
        overflow: 'hidden',
      }}
    >
      <Snackbar
        open={Boolean(saveErr)}
        autoHideDuration={10000}
        onClose={handleSaveErrSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 56, md: 64 } }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setSaveErr(null)}
          sx={{
            width: '100%',
            maxWidth: { xs: 'min(480px, 92vw)', sm: 560 },
            whiteSpace: 'normal',
            alignItems: 'flex-start',
            boxShadow: 4,
          }}
        >
          {saveErr}
        </Alert>
      </Snackbar>
      {toolbar}
      <MailingFlowLinkedCampaignsContext.Provider value={linkedCampaignLine}>
        <MailingFlowCampaignSubscriberListContext.Provider value={linkedSubscriberListIds}>
          <ReactFlowProvider>
            <FlowCanvas
              campaign={campaign}
              name={name}
              linkedContextLoaded={linkedContextLoaded}
              readOnly={chainReadOnly}
              onSaved={onSaved}
              onExitAfterSuccessfulSave={goToCampaignsList}
              onExitFromReadOnly={goToChainsList}
              onSaveError={(msg) => setSaveErr(msg)}
            />
          </ReactFlowProvider>
        </MailingFlowCampaignSubscriberListContext.Provider>
      </MailingFlowLinkedCampaignsContext.Provider>
    </Box>
  );
};

export default MailingFlowEditorPage;
